import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
// Design-system global stylesheet — side-effect import, exactly once, at
// the app entry. This is what applies the DS reset, body typography
// (system-font stack), token-driven colours, focus + selection styles.
// The DS barrel (`@agentisai/design-system`) intentionally does NOT
// re-export it (vanilla-extract `globalStyle()` is side-effect-only), so
// it's imported by its source path. Omitting this is a SILENT failure:
// components render but the page chrome (fonts, page background, link
// colours) is missing.
import '../../../../libs/design-system/src/global.css';
// Client config — imported FIRST (before any UI) so a mis-configured
// .env.local fails fast at boot (the Zod throw in config.ts) instead of
// silently sending users to a broken /authorize redirect.
import './config';
// CloudWatch RUM (S17a / ADR-0027) — initialised BEFORE the React tree
// renders so the very first page-view + any synchronous render-time
// errors are captured. No-op in local dev (config.rum is undefined) and
// idempotent under StrictMode's double-mount.
import { initRum } from './observability/rum';
initRum();
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './auth/AuthProvider';
import { ErrorBoundary } from './components/ErrorBoundary';
import { queryClient } from './queries/queryClient';
import { App } from './App';

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found — check apps/ui/web/index.html.');
}

createRoot(rootEl).render(
  <StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <QueryClientProvider client={queryClient}>
            <App />
          </QueryClientProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </StrictMode>,
);
